/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: AiToolProfilesPage.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the AI Integration module.
----------------------------------------------------------------*/


import { useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Wrench, X, Edit, Copy, Trash2 } from 'lucide-react';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';

import DataGridSimple from '../../components/ui/DataGridSimple';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { useNotifications } from '../../contexts/NotificationContext';
import { useDataGrid, type DataGridFetchResult } from '../../hooks/useDataGrid';
import { ModulePageContainer } from '../../components/layouts';
import { listToolProfiles, deleteToolProfile, AiToolProfile } from './api/aiToolProfilesApi';
import type { AiAgentToolInput } from '../ai-agents/api/aiAgentToolsApi';
import { useI18n } from '../../hooks/useI18n';
import { useAiToolProfilesColumns } from './hooks/useAiToolProfilesColumns';

// -- Preset Interface ----------------------------------------------------------

export interface ToolProfilePreset {
    id: string;
    name: string;
    category: string;
    description: string;
    emoji: string;
    color: string;
    profileName: string;
    profileDescription: string;
    tools?: AiAgentToolInput[];
}

// -- Preset Catalog ------------------------------------------------------------

export const TOOL_PROFILE_PRESETS: ToolProfilePreset[] = [
    // -- Scheduling --------------------------------------------------------------
    {
        id: 'calendly', name: 'Calendly', category: 'Scheduling', emoji: '??',
        color: 'bg-cyan-500/10 border-cyan-500/30 text-cyan-600',
        description: 'Check availability and book appointments via Calendly.',
        profileName: 'Calendly Scheduling',
        profileDescription: 'Allows the AI agent to check available times and schedule appointments using Calendly.',
        tools: [
            {
                name: 'get_available_times', enabled: true,
                description: 'Fetches available appointment time slots from Calendly for a given event type.',
                parameters: { type: 'object', properties: { event_type_uuid: { type: 'string', description: 'The Calendly event type UUID' }, date_range_start: { type: 'string', description: 'Start date YYYY-MM-DD' }, date_range_end: { type: 'string', description: 'End date YYYY-MM-DD' } }, required: ['event_type_uuid', 'date_range_start', 'date_range_end'] },
                endpointUrl: 'https://api.calendly.com/event_type_available_times', httpMethod: 'GET',
                authType: 'bearer', authHeader: 'Authorization', authValue: '', timeoutMs: 8000,
            },
            {
                name: 'schedule_appointment', enabled: true,
                description: 'Schedules an appointment in Calendly with the caller details.',
                parameters: { type: 'object', properties: { event_type_uuid: { type: 'string', description: 'Event type UUID' }, start_time: { type: 'string', description: 'ISO 8601 datetime' }, name: { type: 'string', description: 'Invitee full name' }, email: { type: 'string', description: 'Invitee email' } }, required: ['event_type_uuid', 'start_time', 'name', 'email'] },
                endpointUrl: 'https://api.calendly.com/one_off_event_types', httpMethod: 'POST',
                authType: 'bearer', authHeader: 'Authorization', authValue: '', timeoutMs: 8000,
            },
        ],
    },
    {
        id: 'google_calendar', name: 'Google Calendar', category: 'Scheduling', emoji: '???',
        color: 'bg-blue-500/10 border-blue-500/30 text-blue-600',
        description: 'Check availability and create events in Google Calendar.',
        profileName: 'Google Calendar',
        profileDescription: 'Enables the AI agent to check availability and create calendar events via Google Calendar API.',
        tools: [
            {
                name: 'check_availability', enabled: true,
                description: 'Checks free/busy status for a calendar within a given time range.',
                parameters: { type: 'object', properties: { calendar_id: { type: 'string', description: 'Calendar ID (email)' }, time_min: { type: 'string', description: 'Start ISO 8601' }, time_max: { type: 'string', description: 'End ISO 8601' } }, required: ['calendar_id', 'time_min', 'time_max'] },
                endpointUrl: 'https://www.googleapis.com/calendar/v3/freeBusy', httpMethod: 'POST',
                authType: 'bearer', authHeader: 'Authorization', authValue: '', timeoutMs: 8000,
            },
            {
                name: 'create_calendar_event', enabled: true,
                description: 'Creates a new event in Google Calendar.',
                parameters: { type: 'object', properties: { summary: { type: 'string', description: 'Event title' }, start_datetime: { type: 'string', description: 'Start ISO 8601' }, end_datetime: { type: 'string', description: 'End ISO 8601' }, attendee_email: { type: 'string', description: 'Attendee email' }, description: { type: 'string', description: 'Event notes' } }, required: ['summary', 'start_datetime', 'end_datetime'] },
                endpointUrl: 'https://www.googleapis.com/calendar/v3/calendars/primary/events', httpMethod: 'POST',
                authType: 'bearer', authHeader: 'Authorization', authValue: '', timeoutMs: 8000,
            },
        ],
    },
    {
        id: 'acuity', name: 'Acuity Scheduling', category: 'Scheduling', emoji: '?',
        color: 'bg-sky-500/10 border-sky-500/30 text-sky-600',
        description: 'Schedule and manage client appointments via Acuity.',
        profileName: 'Acuity Scheduling',
        profileDescription: 'Book and manage client appointments using the Acuity Scheduling API.',
        tools: [
            {
                name: 'get_available_dates', enabled: true,
                description: 'Returns available appointment dates for a given type in Acuity.',
                parameters: { type: 'object', properties: { appointment_type_id: { type: 'integer', description: 'Acuity appointment type ID' }, month: { type: 'string', description: 'Month YYYY-MM' } }, required: ['appointment_type_id', 'month'] },
                endpointUrl: 'https://acuityscheduling.com/api/v1/availability/dates', httpMethod: 'GET',
                authType: 'basic', authHeader: 'Authorization', authValue: '', timeoutMs: 8000,
            },
            {
                name: 'book_acuity_appointment', enabled: true,
                description: 'Books a new appointment in Acuity Scheduling.',
                parameters: { type: 'object', properties: { appointment_type_id: { type: 'integer', description: 'Appointment type ID' }, datetime: { type: 'string', description: 'ISO 8601 datetime' }, first_name: { type: 'string', description: 'Client first name' }, last_name: { type: 'string', description: 'Client last name' }, email: { type: 'string', description: 'Client email' }, phone: { type: 'string', description: 'Client phone' } }, required: ['appointment_type_id', 'datetime', 'first_name', 'last_name', 'email'] },
                endpointUrl: 'https://acuityscheduling.com/api/v1/appointments', httpMethod: 'POST',
                authType: 'basic', authHeader: 'Authorization', authValue: '', timeoutMs: 8000,
            },
        ],
    },
    {
        id: 'microsoft_bookings', name: 'Microsoft Bookings', category: 'Scheduling', emoji: '??',
        color: 'bg-blue-700/10 border-blue-700/30 text-blue-700',
        description: 'Schedule appointments using Microsoft Bookings.',
        profileName: 'Microsoft Bookings',
        profileDescription: 'Integrates with Microsoft Bookings to schedule and manage service appointments.',
        tools: [
            {
                name: 'get_bookings_slots', enabled: true,
                description: 'Gets available slots from Microsoft Bookings for a given service.',
                parameters: { type: 'object', properties: { business_id: { type: 'string', description: 'Bookings business ID' }, service_id: { type: 'string', description: 'Service ID' }, start_date: { type: 'string', description: 'Start YYYY-MM-DD' }, end_date: { type: 'string', description: 'End YYYY-MM-DD' } }, required: ['business_id', 'service_id', 'start_date', 'end_date'] },
                endpointUrl: 'https://graph.microsoft.com/v1.0/solutions/bookingBusinesses/{business_id}/getStaffAvailability', httpMethod: 'POST',
                authType: 'bearer', authHeader: 'Authorization', authValue: '', timeoutMs: 8000,
            },
            {
                name: 'create_bookings_appointment', enabled: true,
                description: 'Creates an appointment in Microsoft Bookings.',
                parameters: { type: 'object', properties: { business_id: { type: 'string', description: 'Business ID' }, service_id: { type: 'string', description: 'Service ID' }, start_datetime: { type: 'string', description: 'Start ISO 8601' }, end_datetime: { type: 'string', description: 'End ISO 8601' }, customer_name: { type: 'string', description: 'Customer name' }, customer_email: { type: 'string', description: 'Customer email' } }, required: ['business_id', 'service_id', 'start_datetime', 'end_datetime', 'customer_name', 'customer_email'] },
                endpointUrl: 'https://graph.microsoft.com/v1.0/solutions/bookingBusinesses/{business_id}/appointments', httpMethod: 'POST',
                authType: 'bearer', authHeader: 'Authorization', authValue: '', timeoutMs: 8000,
            },
        ],
    },
    // -- CRM ---------------------------------------------------------------------
    {
        id: 'hubspot', name: 'HubSpot CRM', category: 'CRM', emoji: '??',
        color: 'bg-orange-500/10 border-orange-500/30 text-orange-600',
        description: 'Create contacts, deals and log calls in HubSpot.',
        profileName: 'HubSpot CRM',
        profileDescription: 'Creates or updates contacts and deals in HubSpot, and logs AI agent call activity.',
        tools: [
            {
                name: 'create_hubspot_contact', enabled: true,
                description: 'Creates or updates a contact in HubSpot CRM with caller information.',
                parameters: { type: 'object', properties: { email: { type: 'string', description: 'Email' }, firstname: { type: 'string', description: 'First name' }, lastname: { type: 'string', description: 'Last name' }, phone: { type: 'string', description: 'Phone' }, company: { type: 'string', description: 'Company' } }, required: ['email'] },
                endpointUrl: 'https://api.hubapi.com/crm/v3/objects/contacts', httpMethod: 'POST',
                authType: 'bearer', authHeader: 'Authorization', authValue: '', timeoutMs: 8000,
            },
            {
                name: 'create_hubspot_deal', enabled: true,
                description: 'Creates a new deal in HubSpot CRM linked to a contact.',
                parameters: { type: 'object', properties: { dealname: { type: 'string', description: 'Deal name' }, dealstage: { type: 'string', description: 'Pipeline stage' }, amount: { type: 'number', description: 'Amount USD' }, contact_id: { type: 'string', description: 'Contact ID to associate' } }, required: ['dealname', 'dealstage'] },
                endpointUrl: 'https://api.hubapi.com/crm/v3/objects/deals', httpMethod: 'POST',
                authType: 'bearer', authHeader: 'Authorization', authValue: '', timeoutMs: 8000,
            },
            {
                name: 'log_hubspot_call', enabled: true,
                description: 'Logs a call activity note to a HubSpot contact record.',
                parameters: { type: 'object', properties: { contact_id: { type: 'string', description: 'Contact ID' }, call_summary: { type: 'string', description: 'Call summary' }, call_duration_seconds: { type: 'integer', description: 'Duration in seconds' } }, required: ['contact_id', 'call_summary'] },
                endpointUrl: 'https://api.hubapi.com/crm/v3/objects/calls', httpMethod: 'POST',
                authType: 'bearer', authHeader: 'Authorization', authValue: '', timeoutMs: 8000,
            },
        ],
    },
    {
        id: 'salesforce', name: 'Salesforce', category: 'CRM', emoji: '??',
        color: 'bg-blue-600/10 border-blue-600/30 text-blue-600',
        description: 'Create leads, contacts and tasks in Salesforce.',
        profileName: 'Salesforce CRM',
        profileDescription: 'Create or update Salesforce objects (leads, contacts, tasks) from AI agent conversations.',
        tools: [
            {
                name: 'create_salesforce_lead', enabled: true,
                description: 'Creates a new Lead record in Salesforce.',
                parameters: { type: 'object', properties: { FirstName: { type: 'string', description: 'First name' }, LastName: { type: 'string', description: 'Last name' }, Email: { type: 'string', description: 'Email' }, Phone: { type: 'string', description: 'Phone' }, Company: { type: 'string', description: 'Company' }, LeadSource: { type: 'string', description: 'Source (e.g. Phone)' } }, required: ['LastName', 'Company'] },
                endpointUrl: 'https://YOUR_INSTANCE.salesforce.com/services/data/v57.0/sobjects/Lead', httpMethod: 'POST',
                authType: 'bearer', authHeader: 'Authorization', authValue: '', timeoutMs: 10000,
            },
            {
                name: 'create_salesforce_task', enabled: true,
                description: 'Creates a follow-up task in Salesforce linked to a record.',
                parameters: { type: 'object', properties: { WhoId: { type: 'string', description: 'Related contact/lead ID' }, Subject: { type: 'string', description: 'Task subject' }, Description: { type: 'string', description: 'Call notes' }, ActivityDate: { type: 'string', description: 'Due date YYYY-MM-DD' } }, required: ['Subject'] },
                endpointUrl: 'https://YOUR_INSTANCE.salesforce.com/services/data/v57.0/sobjects/Task', httpMethod: 'POST',
                authType: 'bearer', authHeader: 'Authorization', authValue: '', timeoutMs: 10000,
            },
        ],
    },
    {
        id: 'zoho_crm', name: 'Zoho CRM', category: 'CRM', emoji: '??',
        color: 'bg-yellow-500/10 border-yellow-500/30 text-yellow-600',
        description: 'Manage leads, contacts and deals in Zoho CRM.',
        profileName: 'Zoho CRM',
        profileDescription: 'Create and update leads, contacts and deals in Zoho CRM from AI agent calls.',
        tools: [
            {
                name: 'create_zoho_lead', enabled: true,
                description: 'Creates a new lead in Zoho CRM.',
                parameters: { type: 'object', properties: { First_Name: { type: 'string', description: 'First name' }, Last_Name: { type: 'string', description: 'Last name' }, Email: { type: 'string', description: 'Email' }, Phone: { type: 'string', description: 'Phone' }, Company: { type: 'string', description: 'Company' } }, required: ['Last_Name', 'Company'] },
                endpointUrl: 'https://www.zohoapis.com/crm/v2/Leads', httpMethod: 'POST',
                authType: 'bearer', authHeader: 'Authorization', authValue: '', timeoutMs: 8000,
            },
        ],
    },
    {
        id: 'pipedrive', name: 'Pipedrive', category: 'CRM', emoji: '??',
        color: 'bg-green-600/10 border-green-600/30 text-green-600',
        description: 'Create deals, persons and activities in Pipedrive.',
        profileName: 'Pipedrive CRM',
        profileDescription: 'Create and manage persons, deals, and activities in Pipedrive from AI agent conversations.',
        tools: [
            {
                name: 'create_pipedrive_person', enabled: true,
                description: 'Creates a new Person (contact) in Pipedrive.',
                parameters: { type: 'object', properties: { name: { type: 'string', description: 'Full name' }, email: { type: 'string', description: 'Email' }, phone: { type: 'string', description: 'Phone' } }, required: ['name'] },
                endpointUrl: 'https://api.pipedrive.com/v1/persons', httpMethod: 'POST',
                authType: 'api_key', authHeader: 'X-Api-Token', authValue: '', timeoutMs: 8000,
            },
            {
                name: 'create_pipedrive_deal', enabled: true,
                description: 'Creates a new Deal in Pipedrive linked to a person.',
                parameters: { type: 'object', properties: { title: { type: 'string', description: 'Deal title' }, person_id: { type: 'integer', description: 'Person ID' }, value: { type: 'number', description: 'Deal value' }, currency: { type: 'string', description: 'Currency (e.g. USD)' } }, required: ['title'] },
                endpointUrl: 'https://api.pipedrive.com/v1/deals', httpMethod: 'POST',
                authType: 'api_key', authHeader: 'X-Api-Token', authValue: '', timeoutMs: 8000,
            },
        ],
    },
    // -- Notifications ------------------------------------------------------------
    {
        id: 'slack', name: 'Slack', category: 'Notifications', emoji: '??',
        color: 'bg-purple-500/10 border-purple-500/30 text-purple-600',
        description: 'Send real-time call notifications to a Slack channel.',
        profileName: 'Slack Notifications',
        profileDescription: 'Sends automated notifications and call summaries to Slack channels from AI agent interactions.',
        tools: [
            {
                name: 'send_slack_message', enabled: true,
                description: 'Sends a message to a Slack channel via an Incoming Webhook.',
                parameters: { type: 'object', properties: { text: { type: 'string', description: 'Message text' }, channel: { type: 'string', description: 'Channel name (e.g. #calls)' } }, required: ['text'] },
                endpointUrl: 'https://hooks.slack.com/services/YOUR/WEBHOOK/URL', httpMethod: 'POST',
                authType: 'none', authHeader: 'Authorization', authValue: '', timeoutMs: 5000,
            },
        ],
    },
    {
        id: 'teams', name: 'Microsoft Teams', category: 'Notifications', emoji: '??',
        color: 'bg-indigo-500/10 border-indigo-500/30 text-indigo-600',
        description: 'Post messages and cards to Microsoft Teams channels.',
        profileName: 'MS Teams Notifications',
        profileDescription: 'Sends call summaries and notifications to Microsoft Teams via Incoming Webhooks.',
        tools: [
            {
                name: 'send_teams_message', enabled: true,
                description: 'Posts a notification card to a Microsoft Teams channel via Incoming Webhook.',
                parameters: { type: 'object', properties: { title: { type: 'string', description: 'Card title' }, text: { type: 'string', description: 'Card body / call summary' }, themeColor: { type: 'string', description: 'Hex color (e.g. 0078D7)' } }, required: ['title', 'text'] },
                endpointUrl: 'https://outlook.office.com/webhook/YOUR_WEBHOOK_URL', httpMethod: 'POST',
                authType: 'none', authHeader: 'Authorization', authValue: '', timeoutMs: 5000,
            },
        ],
    },
    {
        id: 'sendgrid', name: 'SendGrid Email', category: 'Notifications', emoji: '??',
        color: 'bg-teal-500/10 border-teal-500/30 text-teal-600',
        description: 'Send confirmation or follow-up emails via SendGrid.',
        profileName: 'SendGrid Email',
        profileDescription: 'Sends transactional emails (confirmations, follow-ups) via SendGrid after AI agent interactions.',
        tools: [
            {
                name: 'send_email', enabled: true,
                description: 'Sends a transactional email via SendGrid to a specified recipient.',
                parameters: { type: 'object', properties: { to_email: { type: 'string', description: 'Recipient email' }, to_name: { type: 'string', description: 'Recipient name' }, subject: { type: 'string', description: 'Subject line' }, body_text: { type: 'string', description: 'Plain text body' } }, required: ['to_email', 'subject', 'body_text'] },
                endpointUrl: 'https://api.sendgrid.com/v3/mail/send', httpMethod: 'POST',
                authType: 'bearer', authHeader: 'Authorization', authValue: '', timeoutMs: 8000,
            },
        ],
    },
    // -- Payments -----------------------------------------------------------------
    {
        id: 'stripe_lookup', name: 'Stripe Lookup', category: 'Payments', emoji: '??',
        color: 'bg-violet-500/10 border-violet-500/30 text-violet-600',
        description: 'Look up customer payment/subscription status in Stripe.',
        profileName: 'Stripe Customer Lookup',
        profileDescription: 'Allows the agent to look up customer payment status, subscription, and invoices from Stripe.',
        tools: [
            {
                name: 'lookup_stripe_customer', enabled: true,
                description: 'Searches Stripe for a customer by email and returns their subscription/payment status.',
                parameters: { type: 'object', properties: { email: { type: 'string', description: 'Customer email to look up' } }, required: ['email'] },
                endpointUrl: 'https://api.stripe.com/v1/customers/search', httpMethod: 'GET',
                authType: 'bearer', authHeader: 'Authorization', authValue: '', timeoutMs: 8000,
            },
            {
                name: 'get_stripe_invoices', enabled: true,
                description: 'Retrieves recent invoices for a Stripe customer ID.',
                parameters: { type: 'object', properties: { customer_id: { type: 'string', description: 'Stripe customer ID (cus_...)' }, limit: { type: 'integer', description: 'Number of invoices (default 5)' } }, required: ['customer_id'] },
                endpointUrl: 'https://api.stripe.com/v1/invoices', httpMethod: 'GET',
                authType: 'bearer', authHeader: 'Authorization', authValue: '', timeoutMs: 8000,
            },
        ],
    },
    // -- Productivity -------------------------------------------------------------
    {
        id: 'google_sheets', name: 'Google Sheets', category: 'Productivity', emoji: '??',
        color: 'bg-green-500/10 border-green-500/30 text-green-600',
        description: 'Append call data or lookup info from a Google Sheet.',
        profileName: 'Google Sheets',
        profileDescription: 'Appends call data to Google Sheets or performs lookups to retrieve information for the caller.',
        tools: [
            {
                name: 'append_to_sheet', enabled: true,
                description: 'Appends a row of call data to a Google Sheet.',
                parameters: { type: 'object', properties: { spreadsheet_id: { type: 'string', description: 'Spreadsheet ID' }, range: { type: 'string', description: 'Range (e.g. Sheet1!A1)' }, values: { type: 'array', description: 'Array of values to append' } }, required: ['spreadsheet_id', 'range', 'values'] },
                endpointUrl: 'https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/{range}:append', httpMethod: 'POST',
                authType: 'bearer', authHeader: 'Authorization', authValue: '', timeoutMs: 8000,
            },
        ],
    },
    {
        id: 'openai_vector_stores', name: 'OpenAI Vector Stores', category: 'Productivity', emoji: '🧠',
        color: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-600',
        description: 'Search an OpenAI Vector Store (RAG) to retrieve specific knowledge.',
        profileName: 'OpenAI Vector Store Search',
        profileDescription: 'Allows the AI agent to search an external OpenAI Vector Store directly via Function Calling for advanced RAG.',
        tools: [
            {
                name: 'search_knowledge_base', enabled: true,
                description: "Queries an OpenAI Vector Store to retrieve context-relevant information.",
                parameters: { type: 'object', properties: { query: { type: 'string', description: 'Search terms or question' } }, required: ['query'] },
                endpointUrl: 'https://api.openai.com/v1/vector_stores/vs_YOUR_VECTOR_STORE_ID/search', httpMethod: 'POST',
                authType: 'inherit_provider', authHeader: 'Authorization', authValue: '', 
                customHeaders: { "OpenAI-Beta": "assistants=v2" },
                timeoutMs: 15000,
            },
        ],
    },
    {
        id: 'openai_lookup', name: 'AI Knowledge Query', category: 'Productivity', emoji: '??',
        color: 'bg-neutral-500/10 border-neutral-500/30 text-neutral-600',
        description: 'Query an external knowledge base or secondary AI model.',
        profileName: 'AI Knowledge Query',
        profileDescription: 'Allows the agent to query an external knowledge base or a secondary AI API for specific answers.',
        tools: [
            {
                name: 'query_knowledge_base', enabled: true,
                description: "Queries an external knowledge base to retrieve information relevant to the caller's question.",
                parameters: { type: 'object', properties: { query: { type: 'string', description: 'Question or search terms' }, context: { type: 'string', description: 'Additional caller context (optional)' } }, required: ['query'] },
                endpointUrl: 'https://your-knowledge-api.example.com/query', httpMethod: 'POST',
                authType: 'bearer', authHeader: 'Authorization', authValue: '', timeoutMs: 10000,
            },
        ],
    },
    // -- Developer ----------------------------------------------------------------
    {
        id: 'webhook', name: 'Custom Webhook', category: 'Developer', emoji: '??',
        color: 'bg-gray-500/10 border-gray-500/30 text-gray-600',
        description: 'Call any custom REST endpoint — maximum flexibility.',
        profileName: 'Custom Webhook',
        profileDescription: 'Generic webhook profile for calling custom REST APIs or internal systems.',
        tools: [
            {
                name: 'call_webhook', enabled: true,
                description: 'Calls a custom webhook endpoint with data extracted from the AI agent conversation.',
                parameters: { type: 'object', properties: { action: { type: 'string', description: 'Action or event type' }, caller_number: { type: 'string', description: 'Caller phone number' }, data: { type: 'object', description: 'Additional JSON data' } }, required: ['action'] },
                endpointUrl: 'https://your-webhook-endpoint.example.com/hook', httpMethod: 'POST',
                authType: 'bearer', authHeader: 'Authorization', authValue: '', timeoutMs: 8000,
            },
        ],
    },
];

const CATEGORIES = ['All', ...Array.from(new Set(TOOL_PROFILE_PRESETS.map(p => p.category)))];

// -- Template Picker Modal -----------------------------------------------------

interface TemplatePickerModalProps {
    onSelect: (preset: ToolProfilePreset | null) => void;
    onClose: () => void;
}

function TemplatePickerModal({ onSelect, onClose }: TemplatePickerModalProps) {
    const [activeCategory, setActiveCategory] = useState('All');

    const filtered = activeCategory === 'All'
        ? TOOL_PROFILE_PRESETS
        : TOOL_PROFILE_PRESETS.filter(p => p.category === activeCategory);

    const modal = (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-4xl max-h-[88vh] flex flex-col overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-border">
                    <div>
                        <h2 className="font-semibold text-foreground flex items-center gap-2">
                            <Wrench className="h-4 w-4 text-primary" />
                            New Tool Profile
                        </h2>
                        <p className="text-xs text-muted-foreground mt-0.5">
                            Choose an integration template — tools come pre-configured and ready to customize
                        </p>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-md hover:bg-accent transition-colors">
                        <X className="h-4 w-4" />
                    </button>
                </div>

                {/* Category Filter */}
                <div className="flex items-center gap-2 px-6 py-3 border-b border-border overflow-x-auto">
                    {CATEGORIES.map(cat => (
                        <button key={cat} onClick={() => setActiveCategory(cat)}
                            className={`px-3 py-1 text-xs rounded-full font-medium whitespace-nowrap transition-colors ${activeCategory === cat
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-muted text-muted-foreground hover:bg-accent hover:text-foreground'}`}>
                            {cat}
                        </button>
                    ))}
                </div>

                {/* Template Grid */}
                <div className="overflow-y-auto flex-1 p-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {/* Blank / Custom option */}
                        {activeCategory === 'All' && (
                            <button
                                onClick={() => onSelect(null)}
                                className="flex items-start gap-3 p-4 rounded-xl border-2 border-dashed border-border hover:border-primary/50 bg-background hover:bg-accent/30 transition-all text-left group"
                            >
                                <span className="text-2xl shrink-0">?</span>
                                <div>
                                    <p className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors">Start from scratch</p>
                                    <p className="text-xs text-muted-foreground mt-0.5">Blank profile — add tools manually</p>
                                </div>
                            </button>
                        )}

                        {filtered.map(preset => (
                            <button key={preset.id} onClick={() => onSelect(preset)}
                                className="flex items-start gap-3 p-4 rounded-xl border border-border hover:border-primary/50 bg-background hover:bg-accent/30 transition-all text-left group">
                                <span className="text-2xl shrink-0 mt-0.5">{preset.emoji}</span>
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2">
                                        <p className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors">{preset.name}</p>
                                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full border font-medium shrink-0 ${preset.color}`}>
                                            {preset.category}
                                        </span>
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{preset.description}</p>
                                    {preset.tools && (
                                        <p className="text-[10px] text-primary/70 mt-1">
                                            {preset.tools.length} tool{preset.tools.length !== 1 ? 's' : ''} included
                                        </p>
                                    )}
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );

    return createPortal(modal, document.body);
}

// -- Page ----------------------------------------------------------------------

export default function AiToolProfilesPage() {
    const navigate = useNavigate();
    const { showSuccess, showError } = useNotifications();
    const { t, refreshKey, i18n } = useI18n();

    // Keep translateRef and notification refs fresh on language change
    // (same pattern as AiProfilesPage)
    const translateRef = useRef(t);
    const showSuccessRef = useRef(showSuccess);
    const showErrorRef = useRef(showError);
    useEffect(() => { translateRef.current = t; }, [t, refreshKey, i18n.language]);
    useEffect(() => {
        showSuccessRef.current = showSuccess;
        showErrorRef.current = showError;
    }, [showSuccess, showError]);

    const { availableColumns } = useAiToolProfilesColumns();
    const [showPicker, setShowPicker] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [profileToDelete, setProfileToDelete] = useState<AiToolProfile | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const fetchData = useCallback(async (params: { page: number; limit: number; sortBy: string; sortOrder: 'asc' | 'desc'; search?: string; }): Promise<DataGridFetchResult> => {
        const res = await listToolProfiles({ page: params.page, limit: params.limit, search: params.search });
        return { data: res.data, total: res.meta.total };
    }, []);

    const { data, loading, columns, pagination, searchQuery, sortBy, sortOrder, handleSearch, handleSort, handlePageChange, handleItemsPerPageChange, handleColumnToggle, refresh } =
        useDataGrid({ fetchData, initialColumns: availableColumns, initialSortBy: 'name', initialSortOrder: 'asc', initialLimit: 10, moduleName: 'aiToolProfiles' });

    const handleCreate = () => setShowPicker(true);
    const handleEdit = (p: AiToolProfile) => navigate(`/admin/ai-integration/tool-profiles/${p.id}`);
    const handleDelete = (p: AiToolProfile) => { setProfileToDelete(p); setShowDeleteModal(true); };

    const handlePickerSelect = (preset: ToolProfilePreset | null) => {
        setShowPicker(false);
        navigate('/admin/ai-integration/tool-profiles/new', { state: { preset } });
    };

    const handleDuplicate = (p: AiToolProfile) => {
        const dupePreset: ToolProfilePreset = {
            id: `dupe-${p.id}`,
            name: p.name,
            category: 'Custom',
            description: p.description ?? '',
            emoji: '??',
            color: 'bg-muted/10 border-muted/30 text-muted-foreground',
            profileName: `${p.name} (copy)`,
            profileDescription: p.description ?? '',
            tools: [],
        };
        navigate('/admin/ai-integration/tool-profiles/new', { state: { preset: dupePreset } });
    };

    const confirmDelete = useCallback(async () => {
        if (!profileToDelete) return;
        setIsDeleting(true);
        try {
            await deleteToolProfile(profileToDelete.id);
            setShowDeleteModal(false);
            setProfileToDelete(null);
            showSuccessRef.current?.(translateRef.current('aiToolProfiles.messages.deleteSuccess'));
            refresh();
        } catch {
            showErrorRef.current?.(translateRef.current('aiToolProfiles.messages.deleteError'));
        } finally {
            setIsDeleting(false);
        }
    }, [profileToDelete, refresh]);

    return (
        <>
            <ModulePageContainer
                currentView="list"
                listView={
                    <DataGridSimple
                        data={data}
                        columns={columns}
                        onColumnToggle={handleColumnToggle}
                        loading={loading}
                        pagination={pagination}
                        onPageChange={handlePageChange}
                        onItemsPerPageChange={handleItemsPerPageChange}
                        searchQuery={searchQuery}
                        onSearch={handleSearch}
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        onSort={handleSort}
                        onAdd={handleCreate}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        renderActions={useCallback((item: AiToolProfile) => (
                            <div className="flex items-center gap-1.5">
                                <SimpleTooltip content={translateRef.current('aiToolProfiles.actions.edit')} position="top">
                                    <button
                                        type="button"
                                        onClick={() => handleEdit(item)}
                                        aria-label={translateRef.current('aiToolProfiles.actions.edit')}
                                        className="p-2 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20 rounded transition-colors">
                                        <Edit className="h-4 w-4" />
                                    </button>
                                </SimpleTooltip>
                                <SimpleTooltip content={translateRef.current('aiToolProfiles.actions.duplicate')} position="top">
                                    <button
                                        type="button"
                                        onClick={() => handleDuplicate(item)}
                                        aria-label={translateRef.current('aiToolProfiles.actions.duplicate')}
                                        className="p-2 text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20 rounded transition-colors">
                                        <Copy className="h-4 w-4" />
                                    </button>
                                </SimpleTooltip>
                                <SimpleTooltip content={translateRef.current('aiToolProfiles.actions.delete')} position="top">
                                    <button
                                        type="button"
                                        onClick={() => handleDelete(item)}
                                        aria-label={translateRef.current('aiToolProfiles.actions.delete')}
                                        className="p-2 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 rounded transition-colors">
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </SimpleTooltip>
                            </div>
                        ), [handleEdit, handleDuplicate, handleDelete])}
                        title={t('aiToolProfiles.title')}
                        titleIcon={<Wrench className="h-5 w-5 text-primary" />}
                        addButtonText={t('aiToolProfiles.actions.add')}
                        searchPlaceholder={t('aiToolProfiles.grid.searchPlaceholder')}
                        emptyMessage={t('aiToolProfiles.grid.emptyMessage')}
                        deleteTitle={t('aiToolProfiles.deleteTitle')}
                        deleteMessage={(item) => t('aiToolProfiles.messages.deleteConfirm', { name: item?.name })}
                        translations={{
                            items: t('common.items'),
                            columns: t('common.columns'),
                            noItemsFound: t('common.noItemsFound'),
                            showing: t('common.showing'),
                            to: t('common.to'),
                            of: t('common.of'),
                            results: t('common.results'),
                            previous: t('common.previous'),
                            next: t('common.next'),
                            page: t('common.page'),
                            deleteItem: t('common.deleteItem'),
                            areYouSureDeleteItem: t('common.areYouSureDeleteItem'),
                            delete: t('common.delete'),
                            cancel: t('common.cancel'),
                            allSelected: t('common.allSelected'),
                            selected: t('common.selected'),
                            searchOptions: t('common.searchOptions'),
                            noOptionsFound: t('common.noOptionsFound'),
                            selectAll: t('common.selectAll'),
                            clearAll: t('common.clearAll')
                        }}
                    />
                }
                formView={null}
            >
                {/* Template Picker Modal */}
                {showPicker && (
                    <TemplatePickerModal
                        onSelect={handlePickerSelect}
                        onClose={() => setShowPicker(false)}
                    />
                )}

                <ConfirmationModal
                    isOpen={showDeleteModal}
                    onClose={() => setShowDeleteModal(false)}
                    onConfirm={confirmDelete}
                    title={t('aiToolProfiles.deleteTitle')}
                    message={
                        profileToDelete
                            ? t('aiToolProfiles.messages.deleteConfirm', { name: profileToDelete.name })
                            : ''
                    }
                    confirmText={t('common.delete')}
                    cancelText={t('common.cancel')}
                    isLoading={isDeleting}
                    type="danger"
                />
            </ModulePageContainer>
        </>
    );
}
